import { useAuthStore } from '@/store/auth.pinia';
import { useSession } from '@/helpers/use/useSessionHelper';

type UseOAuth = {
  /**
   * Opens OAuth popup for web platforms
   * @param urlPopup The OAuth URL to open
   */
  webPopupOAuth: (urlPopup: string) => void;
  /**
   * Opens OAuth popup for mobile platforms using Cordova's InAppBrowser
   * @param urlPopup The OAuth URL to open
   */
  nativePopupOAuth: (urlPopup: string) => void;
};

export function useOAuth(): UseOAuth {
  let inAppBrowserRef: any | undefined = undefined;
  const { isAuth, accessTokenByCodeOld, setAuthData, setAuthType } = useAuthStore();

  async function _handleAuth() {
    if (!isAuth()) {
      await accessTokenByCodeOld();
    }

    if (isAuth()) {
      await useSession().setupApp({ newWebSocket: true, newPush: true, skipRequirementsCheck: false });
    }
  }

  function loadStopCallBack(event: { url: string }) {
    if (inAppBrowserRef && event.url.includes('callback')) {
      inAppBrowserRef.executeScript(
        {
          code: 'getInfo()',
        },
        async function (text: string) {
          try {
            const data = JSON.parse(text);
            console.debug('[DEBUG] Data:', data);
            setAuthData({
              homeAccessToken: data.accessToken,
              homeValidUntil: data.validUntil,
              companyRowId: data.companyRowId,
              coreId: data.coreId,
              userId: data.userId,
              userRowId: data.userRowId,
            });
            /** @see docs/auth_matrix.md */
            setAuthType('External');
          } catch (e) {
            alert(e);
          }

          inAppBrowserRef.close();
        }
      );
    }
  }

  function nativePopupOAuth(urlPopup: string): void {
    inAppBrowserRef = (window as any).cordova.InAppBrowser.open(
      urlPopup,
      '_blank',
      'location=no,toolbar=no,clearcache=yes,clearsessioncache=yes'
    );
    inAppBrowserRef.addEventListener('loadstop', loadStopCallBack);

    inAppBrowserRef.addEventListener('exit', async () => {
      await _handleAuth();
    });
  }

  async function handleMessage(event: MessageEvent, popupWindow: Window) {
    let data;
    try {
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      }
    } catch (e) {
      console.error('[ERROR] Error parsing event data:', e);
    }

    if (data) {
      popupWindow.close();
      window.removeEventListener('message', () => console.log('[INFO] Remove eventListener message'));
      setAuthData({
        homeAccessToken: data.accessToken,
        homeValidUntil: data.validUntil,
        companyRowId: data.companyRowId,
        coreId: data.coreId,
        userId: data.userId,
        userRowId: data.userRowId,
      });

      /** @see docs/auth_matrix.md */
      setAuthType('External');

      await _handleAuth();
    }
  }

  function webPopupOAuth(urlPopup: string): void {
    const popupWindow = window.open(
      urlPopup,
      'popupOAuth',
      'scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=700'
    );

    if (popupWindow) {
      popupWindow.focus();

      window.addEventListener('message', async (event) => {
        await handleMessage(event, popupWindow);
      });
    } else {
      alert('Pop-up windows are blocked. Please allow pop-up windows for this site.');
    }
  }

  return {
    webPopupOAuth,
    nativePopupOAuth,
  };
}
